<template lang="pug">
  // Desktop menu
  v-menu(v-if="!$vuetify.breakpoint.mobile" v-model="showMenu" offset-y rounded="lg" min-width="240" max-width="640" :close-on-content-click="false" left max-height="444" nudge-bottom="4")
    template(v-slot:activator="{ on }")
      v-btn.round-md(v-on="on" color="white" depressed)
        .text-sm.text-none {{ displayText }}
        v-icon(right) $yzi-chevron-down
    .white
      v-skeleton-loader(v-if="isLoading" type="list-item-three-line" width="200")
      div(v-else)
        // Search input - sticky
        .sticky-search.white.pa-2
          v-text-field.round-xl(v-model="searchQuery" placeholder="Search doctors..." hide-details dense outlined clearable)

        .scrollable-content(style="max-height: 350px; overflow-y: auto;")
          // Select All option
          .hover-bg(v-if="!searchQuery" @click="toggleSelectAll")
            .d-flex.align-center.justify-space-between.px-4.py-3
              .text-sm.font-weight-medium All doctors
              v-checkbox(:input-value="isAllSelected" :indeterminate="isIndeterminate" hide-details dense @click.stop="toggleSelectAll")
            v-divider.basic300

          // Individual provider options
          template(v-if="filteredProviders.length")
            .hover-bg(v-for="provider in filteredProviders" :key="provider.value" @click="toggleProvider(provider)")
              .d-flex.align-center.justify-space-between.px-4.py-3
                div
                  .text-sm {{ provider.label }}
                  .text-xs.neutral500--text(v-if="isHq") {{ provider.company }}
                v-checkbox(:input-value="isProviderSelected(provider)" hide-details dense @click.stop="toggleProvider(provider)")
              v-divider.basic300
          template(v-else)
            .px-4.py-3.text-sm.neutral500--text No doctor found

  // Mobile bottom sheet
  div(v-else)
    v-btn.round-md(color="white" depressed @click="showMenu = true")
      .text-sm.text-none {{ displayText }}
      v-icon(right) $yzi-chevron-down

    v-bottom-sheet(v-model="showMenu" max-width="100%")
      v-card
        v-card-text.pa-0
          v-skeleton-loader(v-if="isLoading" type="list-item-three-line")
          div(v-else)
            // Search input - sticky
            .sticky-search.white.px-2.pb-2.pt-4
              v-text-field.round-xl(v-model="searchQuery" placeholder="Search doctors..." hide-details dense outlined clearable)

            .scrollable-content(style="max-height: 400px; overflow-y: auto;")
              // Select All option
              .hover-bg(v-if="!searchQuery" @click="toggleSelectAll")
                .d-flex.align-center.justify-space-between.px-4.py-3
                  .text-sm.font-weight-medium All doctors
                  v-checkbox(:input-value="isAllSelected" :indeterminate="isIndeterminate" hide-details dense @click.stop="toggleSelectAll")
                v-divider.basic300

              // Individual provider options
              template(v-if="filteredProviders.length")
                .hover-bg(v-for="provider in filteredProviders" :key="provider.value" @click="toggleProvider(provider)")
                  .d-flex.align-center.justify-space-between.px-4.py-3
                    div
                      .text-sm {{ provider.label }}
                      .text-xs.neutral500--text(v-if="isHq") {{ provider.company }}
                    v-checkbox(:input-value="isProviderSelected(provider)" hide-details dense @click.stop="toggleProvider(provider)")
                  v-divider.basic300
              template(v-else)
                .px-4.py-3.text-sm.neutral500--text No doctor found
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { debounce } from '@/utils'

export default {
  name: 'ProviderDropdown',
  data: () => ({
    isLoading: true,
    showMenu: false,
    providers: [],
    selectedProviders: [],
    currentRequest: null,
    searchQuery: ''
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
      selectedProvidersFromStore: 'insight/getSelectedProviders',
      selectedCompanies: 'insight/getSelectedCompanies',
      isHq: 'hq/getIsHq'
    }),
    displayText() {
      if (this.selectedProviders.length === 0) {
        return 'All doctors'
      } else if (this.selectedProviders.length === this.providers.length) {
        return 'All doctors'
      } else if (this.selectedProviders.length === 1) {
        return this.selectedProviders[0].label
      } else {
        return `${this.selectedProviders.length} doctors selected`
      }
    },
    isAllSelected() {
      return this.providers.length > 0 && this.selectedProviders.length === this.providers.length
    },
    isIndeterminate() {
      return this.selectedProviders.length > 0 && this.selectedProviders.length < this.providers.length
    },
    filteredProviders() {
      if (!this.searchQuery || this.searchQuery.trim() === '') {
        return this.providers
      }
      const query = this.searchQuery.toLowerCase().trim()
      return this.providers.filter(provider =>
        provider.label.toLowerCase().includes(query)
      )
    }
  },
  mounted() {
    this.fetchProviders()
    if (this.selectedProvidersFromStore) {
      this.selectedProviders = [...this.selectedProvidersFromStore]
    }
  },
  beforeDestroy () {
    // Cancel any pending request when component is destroyed
    if (this.currentRequest) {
      this.currentRequest.abort()
    }
  },
  watch: {
    selectedProvidersFromStore: {
      handler(newValue) {
        if (newValue) {
          this.selectedProviders = [...newValue]
        } else {
          this.selectedProviders = []
        }
      },
      deep: true
    },
    selectedCompanies: {
      handler: debounce(function () {
        this.fetchProviders()
        this.selectedProviders = []
        this.updateStore()
      }, 750),
      deep: true
    },
    showMenu(newValue, oldValue) {
      // When menu is closed (newValue is false) and was previously open (oldValue is true)
      if (!newValue && oldValue) {
        // Clear search query when menu is closed
        this.searchQuery = ''

        // If no providers are selected, select all providers
        if (this.selectedProviders.length === 0) {
          this.selectedProviders = [...this.providers]
          this.updateStore()
        }
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),

    fetchProviders(stopRefetch) {
      // Cancel any existing request
      if (this.currentRequest) {
        this.currentRequest.abort()
      }

      this.isLoading = true

      // Create new AbortController for this request
      const controller = new AbortController()
      this.currentRequest = controller

      const params = {
        fields: 'id,name,company',
        expand: 'company',
        page_size: -1
      }
      if (this.isHq) {
        if (this.selectedCompanies) {
          params.companies = this.selectedCompanies.map(c => c.id).join(',')
        } else {
          params.hq = this.company.id
        }
      }
      this.$axios.get(`/clinics/${this.company.slug}/providers`, {
        params,
        signal: controller.signal
      })
        .then(resp => {
          if (!resp.data?.results) {
            this.providers = []
            this.isLoading = false
            this.currentRequest = null
            return
          }

          this.providers = resp.data.results.map(item => {
            return {
              label: item.name,
              value: `${item.id}`,
              id: item.id,
              company: item.company.name
            }
          })

          // If no providers are selected initially, select all providers by default
          if (this.selectedProviders.length === 0) {
            this.selectedProviders = [...this.providers]
            this.updateStore()
          }

          this.isLoading = false
          this.currentRequest = null
        })
        .catch(error => {
          // Don't handle aborted requests as errors
          if (error.name === 'AbortError') {
            return
          }

          if (!stopRefetch) {
            this.fetchProviders(true)
          } else {
            this.isLoading = false
            this.currentRequest = null
          }
        })
    },
    isProviderSelected(provider) {
      return this.selectedProviders.some(selected => selected.id === provider.id)
    },
    toggleProvider(provider) {
      const index = this.selectedProviders.findIndex(selected => selected.id === provider.id)
      if (index > -1) {
        this.selectedProviders.splice(index, 1)
      } else {
        this.selectedProviders.push(provider)
      }
      this.updateStore()
    },
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedProviders = []
      } else {
        this.selectedProviders = [...this.providers]
      }
      this.updateStore()
    },
    updateStore() {
      // If all providers are selected or none are selected, set to null (All doctors)
      if (this.selectedProviders.length === 0 || this.selectedProviders.length === this.providers.length) {
        this.$store.commit('insight/setSelectedProviders', null)
      } else {
        // Store the selected providers array
        this.$store.commit('insight/setSelectedProviders', [...this.selectedProviders])
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  }
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
}

.sticky-search {
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
  position: sticky;
  top: 0;
  z-index: 1;
}
</style>